import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from '@mantine/core';
import clsx from 'clsx';

import { Button } from '../buttons/Button';
import { ErrorMessage } from '../error-message/ErrorMessage';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import styles from './changePasswordForm.module.scss';

export type ChangePasswordFormProps = {
  onBackToLogin: () => void;
  onSubmit: (newPassword: string) => void;
  backLabel: string;
  passwordError?: string;
  generalError?: string;
};

const passwordCharacterGroups = ['a A', '0 - 9', '*?&-'];

export function ChangePasswordForm({
  onBackToLogin,
  onSubmit,
  backLabel,
  passwordError,
  generalError,
}: ChangePasswordFormProps) {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errorConfirmation, setErrorConfirmation] = useState<string>();

  const canSubmit = newPassword !== '' && confirmation !== '';
  return (
    <div data-testid="change-password-form" className={styles['root']}>
      <span>{t('app.changePasswordWidget.instructions')}</span>
      <div className={styles['input-section']}>
        <TextInput
          label={t('app.changePasswordWidget.newPasswordLabel')}
          placeholder={t('app.changePasswordWidget.placeholder')}
          type="password"
          value={newPassword}
          error={passwordError}
          onChange={(event) => setNewPassword(event.currentTarget.value)}
          classNames={{
            error: styles['input-section__error'],
            input: styles['input-section__text-input'],
          }}
        />
        <TextInput
          label={t('app.changePasswordWidget.confirmationLabel')}
          placeholder={t('app.changePasswordWidget.placeholder')}
          type="password"
          value={confirmation}
          onChange={(event) => {
            setConfirmation(event.currentTarget.value);
            setErrorConfirmation(undefined);
          }}
          error={errorConfirmation}
          classNames={{
            error: styles['input-section__error'],
            input: styles['input-section__text-input'],
          }}
        />
      </div>
      <div className={styles['password-recommendations']}>
        <div className={styles['password-recommendation-character-count']}>
          <span
            className={styles['password-recommendation-character-count__stars']}
          >
            **********
          </span>
          <span
            className={
              styles[
                'password-recommendation-character-count__character-count-description'
              ]
            }
          >
            {t('app.changePasswordWidget.recommendations.characterCount')}
          </span>
        </div>
        <div
          className={styles['password-recommendation-recommended-characters']}
        >
          <span
            className={
              styles[
                'password-recommendation-recommended-characters__description'
              ]
            }
          >
            {t('app.changePasswordWidget.recommendations.containing')}
          </span>
          {passwordCharacterGroups.map((characters, index) => (
            <span
              key={index}
              className={
                styles[
                  'password-recommendation-recommended-characters__character-group'
                ]
              }
            >
              {characters}
            </span>
          ))}
        </div>
      </div>
      {generalError && <ErrorMessage>{generalError}</ErrorMessage>}
      <div className={clsx(styles['button-section'])}>
        <Button
          icon={<GoogleFontIcon name="arrow_back_ios_new" small />}
          variant="text"
          onClick={onBackToLogin}
        >
          {backLabel}
        </Button>
        <Button
          onClick={() => {
            if (newPassword === confirmation) {
              onSubmit(newPassword);
            } else {
              setErrorConfirmation(
                t('app.changePasswordWidget.confirmationError')
              );
            }
          }}
          disabled={!canSubmit}
        >
          {t('app.changePasswordWidget.submit')}
        </Button>
      </div>
    </div>
  );
}
